<template>
  <div>
    <label :for="id" class="block text-sm/6 font-medium text-gray-900 dark:text-white">
      {{ label }}
    </label>
    <div class="mt-2 grid grid-cols-1 gap-2">
      <select
        :id="id"
        :name="name"
        :value="modelValue"
        :class="selectClass"
        :disabled="loading"
        :aria-invalid="ariaInvalid"
        :aria-describedby="errorId"
        @change="onChange"
      >
        <option disabled value="">
          {{ loading ? 'Loading products…' : 'Select a product…' }}
        </option>
        <option v-for="p in products" :key="p.productId" :value="p.productId">
          {{ productOptionLabel(p) }}
        </option>
      </select>
      <p v-if="productsError" class="text-sm text-red-600 dark:text-red-400">
        {{ productsError }}
      </p>
      <p
        v-else-if="showEmptyMessage && emptyMessage"
        class="text-sm text-gray-500 dark:text-gray-400"
      >
        {{ emptyMessage }}
      </p>
    </div>
    <p v-if="error" class="mt-2 text-sm text-red-600 dark:text-red-400" :id="errorId">
      {{ error }}
    </p>
    <slot v-else name="helper" />
  </div>
</template>

<script setup lang="ts">
import type {Product} from '@/api/products';
import {getCountryInfo, normalizeCountryCode} from '@/lib/country';
import {cloudProvider} from '@/lib/providers';

type Props = {
  modelValue: string;
  products: Product[];
  loading: boolean;
  selectClass: string;
  ariaInvalid?: boolean;
  error?: string;
  productsError?: string;
  emptyMessage?: string;
  showEmptyMessage?: boolean;
  id?: string;
  name?: string;
  label?: string;
  errorId?: string;
};

withDefaults(defineProps<Props>(), {
  ariaInvalid: false,
  error: '',
  productsError: '',
  emptyMessage: '',
  showEmptyMessage: false,
  id: 'productId',
  name: 'productId',
  label: 'Product',
  errorId: 'productId-error',
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

function onChange(event: Event) {
  emit('update:modelValue', (event.target as HTMLSelectElement).value);
}

function productCountryFlags(product: Product): string {
  const datacenters =
    product.datacenters?.length
      ? product.datacenters
      : product.supportedDatacenters?.length
        ? product.supportedDatacenters
        : [];
  const countryCodes =
    datacenters.length > 0
      ? datacenters.map((dc) => dc?.country)
      : product.countries?.length
        ? product.countries
        : product.supportedCountries || [];
  const uniq = new Set<string>();
  const flags: string[] = [];
  for (const code of countryCodes ?? []) {
    const normalized = normalizeCountryCode(code);
    if (!normalized || uniq.has(normalized)) continue;
    uniq.add(normalized);
    flags.push(getCountryInfo(normalized).flag);
  }
  return flags.join(' ');
}

function productOptionLabel(product: Product): string {
  const flags = productCountryFlags(product);
  const suffix = flags ? ` • ${flags}` : '';
  // Only some DigitalOcean sizes have NVMe disks.
  const disk = cloudProvider(product.provider) === 'digitalocean' ? 'SSD' : 'NVMe';
  return `${product.cpuArchitecture.short} • ${product.name} • ${product.cpus} vCPU • ${product.memory} GB RAM • ${product.disk} GB ${disk}${suffix}`;
}
</script>
